<template>
    <v-container fluid class="pa-4">
        <v-row v-if="loading">
            <v-col cols="12" class="text-center pa-12">
                <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            </v-col>
        </v-row>

        <template v-else-if="dashboard">
            <v-row dense>
                <v-col cols="12">
                    <v-card class="pa-4 mb-3" color="primary" dark>
                        <div class="text-h5">
                            Ciao {{ welcomeName }} 👋
                        </div>
                        <div class="text-subtitle-1">
                            {{ dashboard.employee ? (dashboard.employee.job_title || 'Dipendente') : 'Utente' }}
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="dashboard.no_employee_linked" dense>
                <v-col cols="12">
                    <v-alert type="warning" prominent border="left" class="mb-3">
                        <div class="text-h6 mb-1">
                            <v-icon left>mdi-account-alert</v-icon>
                            Profilo dipendente non collegato
                        </div>
                        <div>
                            Il tuo utente non è ancora associato ad una scheda dipendente HR.
                            Per questo motivo non sono disponibili le informazioni personali (contratto,
                            residui ferie, cedolini, cartellino).
                        </div>
                        <div class="mt-2">
                            <strong>Cosa fare:</strong> contatta l'amministratore del sistema o
                            l'ufficio HR per richiedere il collegamento del tuo account ad una scheda dipendente.
                            Nel frattempo puoi consultare gli avvisi aziendali nella bacheca qui sotto.
                        </div>
                    </v-alert>
                </v-col>
            </v-row>

            <v-row v-if="announcements.length" dense>
                <v-col cols="12">
                    <v-card class="pa-4 mb-3">
                        <div class="d-flex align-center mb-3">
                            <v-icon left color="primary">mdi-bullhorn</v-icon>
                            <span class="text-h6">Bacheca avvisi</span>
                            <v-spacer></v-spacer>
                            <v-chip x-small outlined color="grey">{{ announcements.length }}</v-chip>
                        </div>

                        <div
                            v-for="a in announcements"
                            :key="a.id"
                            class="announcement-card"
                            :class="[`severity-${a.severity || 'info'}`, { 'is-pinned': a.pinned }]">
                            <div class="announcement-header">
                                <div class="d-flex align-center flex-grow-1">
                                    <v-icon :color="severityColor(a.severity)" small class="mr-2">{{ severityIcon(a.severity) }}</v-icon>
                                    <span class="announcement-title">{{ a.title }}</span>
                                    <v-icon v-if="a.pinned" small color="amber darken-2" class="ml-2" title="In evidenza">mdi-pin</v-icon>
                                    <v-chip
                                        x-small
                                        outlined
                                        :color="severityColor(a.severity)"
                                        class="ml-2 severity-chip">
                                        {{ severityLabel(a.severity) }}
                                    </v-chip>
                                </div>
                                <span class="announcement-date">
                                    <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                    {{ formatDate(a.published_at) }}
                                </span>
                            </div>
                            <div class="announcement-body" v-html="sanitizeRichHtml(a.body)"></div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="!dashboard.no_employee_linked" dense>
                <v-col cols="12" md="6">
                    <hr-clock-widget self-service embedded></hr-clock-widget>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card class="pa-3 mb-3">
                        <div class="text-h6 mb-2">
                            <v-icon left color="primary">mdi-file-sign</v-icon>
                            Contratto attivo
                        </div>
                        <template v-if="dashboard.contract">
                            <div><strong>Tipo:</strong> {{ dashboard.contract.type }}</div>
                            <div><strong>CCNL:</strong> {{ dashboard.contract.ccnl_code || '—' }}</div>
                            <div><strong>Livello:</strong> {{ dashboard.contract.level || '—' }}</div>
                            <div><strong>Ore settimanali:</strong> {{ dashboard.contract.working_hours_per_week }}</div>
                            <div>
                                <strong>Periodo:</strong>
                                {{ formatDate(dashboard.contract.start_date) }} →
                                {{ dashboard.contract.end_date ? formatDate(dashboard.contract.end_date) : 'in corso' }}
                            </div>
                            <v-alert v-if="dashboard.contract.expiring_soon" type="warning" dense text class="mt-2">
                                Contratto in scadenza nei prossimi 30 giorni.
                            </v-alert>
                        </template>
                        <v-alert v-else type="info" dense text>Nessun contratto attivo.</v-alert>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card class="pa-3 mb-3">
                        <div class="text-h6 mb-2">
                            <v-icon left color="primary">mdi-cash</v-icon>
                            Ultimo cedolino
                        </div>
                        <template v-if="dashboard.last_payslip">
                            <div>
                                <strong>Periodo:</strong>
                                {{ String(dashboard.last_payslip.period_month).padStart(2, '0') }}/{{ dashboard.last_payslip.period_year }}
                            </div>
                            <div><strong>Lordo:</strong> €{{ formatAmount(dashboard.last_payslip.gross_amount) }}</div>
                            <div><strong>Netto:</strong> €{{ formatAmount(dashboard.last_payslip.net_amount) }}</div>
                            <v-btn small text color="primary" class="mt-2" :to="{ path: '/hrpayslips' }">
                                <v-icon left small>mdi-folder-open</v-icon>
                                Tutti i cedolini
                            </v-btn>
                        </template>
                        <v-alert v-else type="info" dense text>Nessun cedolino disponibile.</v-alert>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card class="pa-3 mb-3">
                        <div class="text-h6 mb-2">
                            <v-icon left color="primary">mdi-beach</v-icon>
                            Residui ore
                        </div>
                        <v-simple-table dense>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th class="text-right">Maturate</th>
                                    <th class="text-right">Usate</th>
                                    <th class="text-right">Residue</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="b in dashboard.balances" :key="b.type_code">
                                    <td>{{ b.type_name }}</td>
                                    <td class="text-right">{{ b.accrued_hours }}h</td>
                                    <td class="text-right">{{ b.used_hours }}h</td>
                                    <td class="text-right font-weight-bold">{{ b.residual_hours }}h</td>
                                </tr>
                                <tr v-if="dashboard.balances.length === 0">
                                    <td colspan="4" class="grey--text text-center">Nessun residuo configurato.</td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card class="pa-3 mb-3">
                        <div class="text-h6 mb-2">
                            <v-icon left color="primary">mdi-calendar-clock</v-icon>
                            Prossime assenze
                        </div>
                        <template v-if="dashboard.upcoming_absences && dashboard.upcoming_absences.length">
                            <div v-for="abs in dashboard.upcoming_absences" :key="abs.id" class="mb-2">
                                <v-chip x-small :color="abs.status === 'pending' ? 'orange' : 'success'" outlined>
                                    {{ abs.status === 'pending' ? 'In attesa' : 'Approvata' }}
                                </v-chip>
                                <span class="ml-2">
                                    <strong v-if="abs.absence_type_name">{{ abs.absence_type_name }}</strong>
                                    {{ formatAbsenceRange(abs.start_at, abs.end_at, abs.half_day) }}
                                    <!-- La pausa non fruita non ha ore -->
                                    <template v-if="abs.absence_type_kind !== 'break_waiver'">({{ abs.total_hours }}h)</template>
                                </span>
                            </div>
                        </template>
                        <v-alert v-else type="info" dense text>Nessuna assenza programmata.</v-alert>
                    </v-card>
                </v-col>
            </v-row>

            <v-row v-if="dashboard.anomalies_this_month && dashboard.anomalies_this_month.length" dense>
                <v-col cols="12">
                    <v-card class="pa-3 mb-3">
                        <div class="text-h6 mb-2">
                            <v-icon left color="warning">mdi-alert</v-icon>
                            Anomalie cartellino — mese in corso
                        </div>
                        <v-alert type="warning" dense text>
                            {{ dashboard.anomalies_this_month.length }} giorni con anomalia. Contatta HR per risolverle.
                        </v-alert>
                        <v-data-table
                            :headers="anomalyHeaders"
                            :items="dashboard.anomalies_this_month"
                            dense
                            hide-default-footer
                            :items-per-page="-1">
                            <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                            <template v-slot:item.minutes_worked="{ item }">{{ formatMinutes(item.minutes_worked) }}</template>
                            <template v-slot:item.anomalies="{ item }">
                                <v-chip v-for="a in (item.anomalies || [])" :key="a" x-small :color="isBreakAnomaly(a) ? 'blue-grey' : 'warning'" outlined class="mr-1">
                                    {{ anomalyLabel(a, item) }}
                                </v-chip>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
        </template>

        <v-alert v-else-if="error" type="error" dense text>
            {{ error }}
        </v-alert>
    </v-container>
</template>

<script>
import HrClockWidget from '@/components/modules/hr/HrClockWidget'
import { formatAbsenceRange } from '@/components/utilities/hrDates'
import { anomalyLabel, isBreakAnomaly } from '@/components/utilities/hrColors'
import { sanitizeRichHtml } from '@/utils/sanitizeHtml'

export default {
    name: 'hr-me',
    components: { HrClockWidget },

    data() {
        return {
            loading: true,
            dashboard: null,
            announcements: [],
            error: null,
            anomalyHeaders: [
                { text: 'Data', value: 'date' },
                { text: 'Ore lavorate', value: 'minutes_worked' },
                { text: 'Anomalie', value: 'anomalies', sortable: false },
            ],
        }
    },

    computed: {
        welcomeName() {
            if (this.dashboard && this.dashboard.employee && this.dashboard.employee.first_name) {
                return this.dashboard.employee.first_name
            }
            const u = this.$store.getters.getProfile || {}
            const fromName = (u.name || '').trim().split(/\s+/)[0]
            return fromName || (u.email ? u.email.split('@')[0] : '')
        },
    },

    created() {
        this.load()
        this.loadAnnouncements()
    },

    methods: {
        sanitizeRichHtml,

        load() {
            this.loading = true
            this.axios.get('hrme/dashboard').then(response => {
                this.dashboard = response.data
                this.loading = false
                // Il loader globale lo spegneva solo il widget timbratura, che
                // senza dipendente collegato non c'è: restava acceso all'infinito
                this.$store.commit('endProgress')
            }, error => {
                this.error = (error.response && error.response.data && error.response.data.message) || 'Errore caricamento dati.'
                this.loading = false
                this.$store.commit('endProgress')
            })
        },

        loadAnnouncements() {
            this.axios.get('hrme/announcements').then(r => {
                this.announcements = (r.data && r.data.data) || []
            }, () => { this.announcements = [] })
        },

        severityColor(s) {
            return { info: 'primary', warning: 'orange', important: 'red', celebration: 'green' }[s] || 'grey'
        },
        severityIcon(s) {
            return {
                info: 'mdi-information',
                warning: 'mdi-alert',
                important: 'mdi-alert-octagon',
                celebration: 'mdi-party-popper',
            }[s] || 'mdi-bullhorn'
        },
        severityClass(s) {
            return s === 'important' ? 'red lighten-5' : (s === 'warning' ? 'orange lighten-5' : '')
        },
        severityLabel(s) {
            return { info: 'Informazione', warning: 'Attenzione', important: 'Importante', celebration: 'Notizia' }[s] || 'Avviso'
        },
        formatDate(s) {
            if (!s) return ''
            const d = new Date(s)
            return d.toLocaleDateString('it-IT')
        },
        formatAbsenceRange,
        formatAmount(n) {
            if (n === null || n === undefined) return '0,00'
            return Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },
        formatMinutes(m) {
            if (!m) return '0h'
            const h = Math.floor(m / 60)
            const mm = m % 60
            return mm ? `${h}h ${String(mm).padStart(2, '0')}m` : `${h}h`
        },
        anomalyLabel,
        isBreakAnomaly,
    },
}
</script>

<style scoped>
.announcement-card {
    position: relative;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    border-left: 4px solid #bdbdbd;
    background: #fafafa;
    padding: 12px 16px;
    margin-bottom: 8px;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.announcement-card:last-child {
    margin-bottom: 0;
}
.announcement-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.announcement-card.is-pinned {
    background: #fffaf0;
    border-color: #ffe0b2;
}
.announcement-card.severity-info {
    border-left-color: #1976d2;
}
.announcement-card.severity-warning {
    border-left-color: #fb8c00;
    background: #fff8e1;
    border-color: #ffe0b2;
}
.announcement-card.severity-important {
    border-left-color: #d32f2f;
    background: #ffebee;
    border-color: #ffcdd2;
}
.announcement-card.severity-celebration {
    border-left-color: #2e7d32;
    background: #e8f5e9;
    border-color: #c8e6c9;
}

.announcement-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
    flex-wrap: wrap;
}
.announcement-title {
    font-size: 15px;
    font-weight: 600;
    color: #212121;
    line-height: 1.3;
}
.announcement-date {
    font-size: 12px;
    color: #757575;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
}
.severity-chip {
    height: 18px !important;
    font-size: 10px !important;
    text-transform: uppercase;
    letter-spacing: 0.4px;
}

.announcement-body {
    font-size: 13px;
    color: #424242;
    line-height: 1.5;
    padding-left: 26px;
}
.announcement-body ::v-deep p {
    margin: 0 0 6px 0;
}
.announcement-body ::v-deep p:last-child {
    margin-bottom: 0;
}
.announcement-body ::v-deep ul,
.announcement-body ::v-deep ol {
    margin: 4px 0 6px 0;
    padding-left: 20px;
}
.announcement-body ::v-deep a {
    color: #1976d2;
    text-decoration: none;
}
.announcement-body ::v-deep a:hover {
    text-decoration: underline;
}
</style>
